import { useMemo } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { Grid, Image } from 'semantic-ui-react'

import {
  FormikInput,
  FormikScrollOnError,
  getFieldName,
  GridRowColumn,
  Link,
  Text,
} from '@/componentsV1/BaseComponents'
import {
  DEDUCTIONS_AND_CREDITS_SCREENS,
  DeductionsAndCreditsStepProps,
} from '.'
import { useReselector } from '@/utils/sharedHooks'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { TaxListQuestionId } from '../../service'
import {
  selectTaxListQuestion,
  selectTaxListQuestionResponsesByFormId,
} from '../../taxChecklist.selectors'
import { TaxChecklistResponse } from '../../taxChecklistQuestion.slice'
import { FormFlowFooter } from '@/components/form-flow/FormFlowFooter'
import { CalculationAlert } from '@/features/taxes/annual-taxes/tax-checklist/shared/SpecialtyAlerts'
import {
  tqBigSpace,
  tqSmallSpace,
} from '@/features/taxes/annual-taxes/tax-checklist/helpers'
import { select1040FormForYear } from '@/features/taxes/annual-taxes/annualTaxFilingForms.selector'
import {
  homeOfficeSquareFootageQuestionIds,
  homeOfficeSquareFootageSchema,
} from './homeOfficeSquareFootage'

export const homeOfficeStandardStep1QuestionIds =
  homeOfficeSquareFootageQuestionIds

const HomeOfficeDeductionsStandardStep1Panel = ({
  goBack,
  goToNextStep,
}: DeductionsAndCreditsStepProps) => {
  const taxYear = useReselector(selectCurrentAnnualTaxYear)
  const form1040 = useReselector(select1040FormForYear, taxYear)
  const formId = form1040?.id

  const questionSquareFootageBusiness = useReselector(
    selectTaxListQuestion,
    TaxListQuestionId.home_office_sq_ft_for_business,
    taxYear
  )
  const responseSquareFootageBusiness = useReselector(
    selectTaxListQuestionResponsesByFormId,
    TaxListQuestionId.home_office_sq_ft_for_business,
    Number(formId)
  )

  const questionSquareFootageTotal = useReselector(
    selectTaxListQuestion,
    TaxListQuestionId.home_office_sq_ft_total,
    taxYear
  )
  const responseSquareFootageTotal = useReselector(
    selectTaxListQuestionResponsesByFormId,
    TaxListQuestionId.home_office_sq_ft_total,
    Number(formId)
  )

  const formik = useFormik({
    initialValues: {
      squareFtUsedForBusiness:
        responseSquareFootageBusiness?.[0]?.value ?? undefined,
      totalSquareFtOfHome: responseSquareFootageTotal?.[0]?.value ?? undefined,
    },
    validationSchema: homeOfficeSquareFootageSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: async (values) => {
      const responseData: Partial<TaxChecklistResponse>[] = []
      if (
        values.squareFtUsedForBusiness !==
        responseSquareFootageBusiness?.[0]?.value
      ) {
        responseData.push({
          id: responseSquareFootageBusiness?.[0]?.id,
          value: Number(values.squareFtUsedForBusiness ?? 0),
          annualTaxFilingFormId: Number(formId),
          questionId: TaxListQuestionId.home_office_sq_ft_for_business,
        })
      }
      if (
        values.totalSquareFtOfHome !== responseSquareFootageTotal?.[0]?.value
      ) {
        responseData.push({
          id: responseSquareFootageTotal?.[0]?.id,
          value: Number(values.totalSquareFtOfHome ?? 0),
          annualTaxFilingFormId: Number(formId),
          questionId: TaxListQuestionId.home_office_sq_ft_total,
        })
      }
      await goToNextStep(
        responseData,
        DEDUCTIONS_AND_CREDITS_SCREENS.homeOfficeStandardStep2
      )
    },
  })

  const { values, submitForm, errors, isSubmitting } = formik

  const percentage = useMemo(
    () =>
      !errors.squareFtUsedForBusiness &&
      !errors.totalSquareFtOfHome &&
      values.squareFtUsedForBusiness &&
      values.totalSquareFtOfHome
        ? (
            (100 * Number(values.squareFtUsedForBusiness ?? 0)) /
            Number(values.totalSquareFtOfHome ?? 0)
          ).toFixed(0)
        : '--',
    [values, errors]
  )

  return (
    <Grid>
      <Grid.Row />
      <Grid.Row />
      <GridRowColumn centerContent {...tqBigSpace}>
        <Image
          src="https://heard-images.s3.amazonaws.com/assets/big-house.svg"
          style={{ margin: 'auto', width: 120 }}
        />
      </GridRowColumn>
      <GridRowColumn {...tqBigSpace}>
        <Text as="display2" textAlign="center">
          Step 1: Standard Method
        </Text>
      </GridRowColumn>
      <GridRowColumn {...tqSmallSpace}>
        <Text as="bodyLg">
          In this two-step process, we’ll ask you for the square footage of your
          home office, as well as total amounts spent across a number of
          categories.
        </Text>
      </GridRowColumn>
      <GridRowColumn {...tqSmallSpace}>
        <Link
          href="https://support.joinheard.com/hc/en-us/articles/10270233571607-Home-Office-Deductions-FAQs"
          newPage
        >
          Home Office Deduction FAQs
        </Link>
      </GridRowColumn>
      <FormikProvider value={formik}>
        <FormikScrollOnError />
        <GridRowColumn {...tqSmallSpace}>
          <FormikInput
            name={getFieldName<typeof values>('squareFtUsedForBusiness')}
            label={questionSquareFootageBusiness?.question?.text}
            placeholder="Enter business square footage"
            fullWidth
          />
        </GridRowColumn>
        <GridRowColumn {...tqSmallSpace}>
          <FormikInput
            name={getFieldName<typeof values>('totalSquareFtOfHome')}
            label={questionSquareFootageTotal?.question?.text}
            placeholder="Enter home square footage"
            fullWidth
          />
        </GridRowColumn>
      </FormikProvider>
      {values.squareFtUsedForBusiness && values.totalSquareFtOfHome && (
        <GridRowColumn short {...tqSmallSpace}>
          <CalculationAlert>
            Your home office allocation is <b>{percentage}%</b>
          </CalculationAlert>
        </GridRowColumn>
      )}
      <FormFlowFooter
        onBack={() => goBack(DEDUCTIONS_AND_CREDITS_SCREENS.homeOffice)}
        onForward={submitForm}
        continueDisabled={isSubmitting}
        loading={isSubmitting}
      />
    </Grid>
  )
}

export default HomeOfficeDeductionsStandardStep1Panel
